import { ITooltipParams, RowClickedEvent } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { type AuditLogWithUser, DateTimeSettings, EXPORT_JOB_TYPES, SYNC_JOB_TYPES } from '@cdw-ae/common-data';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import {
  CdwDrawerModule,
  closeDrawer,
  DrawerNotificationService,
  openDrawer,
} from '@cdw-ae/common-ui-components-drawer';
import {
  CdwDrawerContentModule,
  DrawerContent,
} from '@cdw-ae/common-ui-components-drawer-content';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import {
  CdwColDef,
  CdwSsrmGridComponent,
  PreviewIconComponent,
} from '@cdw-ae/common-ui-components-search-grid';
import {
  UserCellRendererComponent,
  UserHoverCardService,
  UserProfileCardComponent,
  type UserProfileCardData,
} from '@cdw-ae/common-ui-components-user-profile-card';
import { DateTimeAngularService } from '@cdw-ae/date-time-angular-service';
import { formatTimeAgo } from '@cdw-ae/date-time-service';
import {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTemporalMatchersModule } from '@cdw-ae/filter-bar-temporal-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { GoogleTagManagerService } from '../../../../../common/services/analytics/google-tag-manager.service';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import * as FilterActions from '../../../../../+state/filter/filter.actions';
import { selectSelectedFilters } from '../../../../../+state/filter/filter.selectors';
import { selectCustomerDateTimeSettings } from '../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { AuditLogFilterSettings } from '../../../../../common/services/route-config/configs/filter-bar-config.config';
import { AdministrationComponent } from '../administration.component';
import { ActionDataCellRendererComponent } from './action-data-cell-renderer.component';
import { AuditLogDataService } from './audit-log-data.service';
import { formatOperationType } from './format-operation-type';

@Component({
  selector: 'audit-log',
  standalone: true,
  imports: [
    CommonModule,
    AdministrationComponent,
    MaterialLibraryModule,
    CdwIconModule,
    CdwDrawerModule,
    CdwDrawerContentModule,
    CdwSsrmGridComponent,
    MatSidenavModule,
    FilterBarTextMatchersModule,
    FilterBarSelectionMatchersModule,
    FilterBarTemporalMatchersModule,
    UserProfileCardComponent,
    ActionDataCellRendererComponent,
  ],
  templateUrl: './audit-log.component.html',
  styleUrl: './audit-log.component.scss',
})
export class AuditLogComponent implements OnInit, OnDestroy {
  @ViewChild('auditLogSearchGrid')
  public auditLogSearchGrid!: CdwSsrmGridComponent<AuditLogWithUser>;

  private readonly destroy$ = new Subject<void>();
  private readonly auditLogFilterBarId = 'auditLogSearch';

  public readonly gray50 = TailwindColors.gray50;
  public readonly gray300 = TailwindColors.gray300;
  public readonly gray400 = TailwindColors.gray400;
  public readonly primaryBlack = TailwindColors.primaryBlack;
  public readonly ctaBlue = TailwindColors.tertiaryBlueCta;

  dateTimeSettings$!: Observable<DateTimeSettings>;
  private currentDateTimeSettings!: DateTimeSettings;

  public filterBarSettings: FilterBarField[] = AuditLogFilterSettings;
  public itemLabel = 'audit log';
  public currentFilters: SelectedFilterField[] = [];

  public drawerContents: DrawerContent[] = [];
  public drawerTitle = '';
  public drawerSubtitle = '';
  public selectedUserId: number | null = null;
  public selectedUserData: UserProfileCardData | null = null;
  public initialPage?: number;

  // Bulk-job drawer state (EDUS-2625) — populated when a job row's eyeball is clicked
  public isBulkJobDrawer = false;
  public bulkJobData: AuditLogWithUser | null = null;

  public fieldMask$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([
    'created_at',
    'name',
    'category',
    // 'job_type', // retired (EDUS-2952) — see AuditLogFilterSettings
    'action',
  ]);

  public columnDefs: CdwColDef[] = [];

  public hoverCardData$ = this.hoverCardService.cardData$;
  public hoverCardPosition$ = this.hoverCardService.position$;

  constructor(
    public auditLogDataService: AuditLogDataService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private drawerNotifications: DrawerNotificationService,
    private hoverCardService: UserHoverCardService,
    private dateTimeService: DateTimeAngularService,
    private gtmService: GoogleTagManagerService,
    private snackbarService: SnackbarService
  ) {
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);

    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        // No grid refresh needed: cells show relative time (formatTimeAgo),
        // and the tooltip/drawer read this field when they render.
        this.currentDateTimeSettings = settings;
      });

    this.columnDefs = [
      {
        field: 'createdAt',
        headerName: 'Date and Time',
        flex: 0.5,
        valueFormatter: (params) => formatTimeAgo(params.value),
        tooltipValueGetter: (params: ITooltipParams) =>
          this.dateTimeService.formatForGrid(
            params.value,
            this.currentDateTimeSettings
          ),
      },
      {
        field: 'user.name',
        headerName: 'User',
        flex: 0.6,
        cellRenderer: UserCellRendererComponent,
        cellRendererParams: {
          iconColor: this.primaryBlack,
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 0.35,
      },
      // Job Type (Standard/NA) retired (EDUS-2952) — see AuditLogFilterSettings
      // {
      //   field: 'jobId',
      //   headerName: 'Job Type',
      //   flex: 0.5,
      //   valueFormatter: (params) => {
      //     return params.value ? 'Standard' : 'NA';
      //   },
      // },
      {
        field: 'action',
        headerName: 'Action',
        flex: 1,
      },
      {
        field: 'actionData',
        headerName: 'Action Data',
        flex: 0.8,
        sortable: false,
        cellRenderer: ActionDataCellRendererComponent,
      },
      {
        field: 'id',
        headerName: 'Details',
        flex: 0.25,
        sortable: false,
        cellRenderer: PreviewIconComponent,
        cellClass: 'ag-cell-left',
        onCellClicked: (event) => {
          this.onRowClicked(event);
        },
      },
    ];
  }

  ngOnInit(): void {
    // Track audit log page view in GTM
    this.gtmService.pushUserAction({
      category: 'reports',
      action: 'audit_log_page_viewed',
      metadata: {
        report_type: 'audit_log',
      },
    });

    const savedPage = sessionStorage.getItem('auditLogReturnPage');
    if (savedPage) {
      this.initialPage = parseInt(savedPage, 10);
      sessionStorage.removeItem('auditLogReturnPage');
    }

    // EDUS-2673: re-open the bulk-job drawer if we're returning from the job-detail page
    const savedDrawer = sessionStorage.getItem('auditLogReturnDrawer');
    if (savedDrawer) {
      sessionStorage.removeItem('auditLogReturnDrawer');
      try {
        const { auditLogData, userData } = JSON.parse(savedDrawer) as {
          auditLogData: AuditLogWithUser;
          userData: UserProfileCardData;
        };
        if (auditLogData?.jobId) {
          queueMicrotask(() => this.onBulkJobClick(auditLogData, userData));
        }
      } catch {
        // Malformed payload — silently drop; user just loses the re-open, page still works.
      }
    }

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: Record<string, string>) => {
        this.store.dispatch(
          FilterActions.loadFiltersFromUrl({ queryParams: params })
        );
      });

    // Sync NgRx filter state to the grid's initialSelectedFilterFields
    this.store
      .select(selectSelectedFilters(this.auditLogFilterBarId))
      // The selector hands back a fresh array whenever any filter bar changes,
      // and the grid purges and refetches on every new reference.
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        this.currentFilters = filters;
        this.hoverCardService.hideCard();
      });

    this.drawerNotifications.closeDrawer$
      .pipe(
        filter(({ id }) => id === 'auditLogDrawer'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Row highlight clears automatically when drawer closes
        this.isBulkJobDrawer = false;
        this.bulkJobData = null;
      });
  }

  // Template helpers for bulk-job drawer (EDUS-2625)
  // EDUS-2673: use absolute date/time (matches Last Ran) instead of relative "X ago"
  get bulkJobCreated(): string {
    if (!this.bulkJobData?.createdAt) return 'Unknown';
    return this.dateTimeService.formatForGrid(
      this.bulkJobData.createdAt,
      this.currentDateTimeSettings
    );
  }

  get bulkJobType(): string {
    const op = this.bulkJobData?.operationType;
    if (!op) return this.bulkJobData?.action || '—';
    return formatOperationType(op);
  }

  get isExportJob(): boolean {
    return EXPORT_JOB_TYPES.has(this.bulkJobData?.operationType ?? '');
  }

  get isSyncJob(): boolean {
    return SYNC_JOB_TYPES.has(this.bulkJobData?.operationType ?? '');
  }

  get bulkJobLastRan(): string {
    if (!this.bulkJobData?.updatedAt) return 'Unknown';
    return this.dateTimeService.formatForGrid(
      this.bulkJobData.updatedAt,
      this.currentDateTimeSettings
    );
  }

  // EDUS-2668: Resources Generated link derived from jobs.additional_job_data
  // (joined server-side). Google Sheets URLs open directly; CSV routes through
  // the OpenRedirectComponent at /open/export/:jobId which triggers the
  // download endpoint. Returns null for non-export bulk jobs so the whole
  // section can be hidden via *ngIf.
  get exportOutputUrl(): string | null {
    const ref = this.bulkJobData?.outputReference;
    if (!ref) return null;
    if (this.bulkJobData?.outputFormat === 'csv') {
      const jobId = this.bulkJobData?.jobId;
      return jobId ? `/open/export/${jobId}` : null;
    }
    return ref;
  }

  get exportOutputLabel(): string {
    return this.bulkJobData?.outputFormat === 'csv'
      ? 'Download Export'
      : 'View in Google Sheets';
  }

  // EDUS-2668: The audit-log job detail page (/administration/audit-log/job/:jobId)
  // fetches asset changes keyed by assetTypeId. When no audit_log_asset_changes
  // rows exist for the job (exports don't produce them), the SQL query returns
  // assetTypeId as NULL and the detail page has nothing to render. Use that as
  // the direct signal — proxies like operationType/action are brittle.
  // EDUS-3090: hidden for cancelled runs — the rows list targets, not what the
  // run reached, and the changes export fails without the post-update snapshot.
  get showJobDetailsButton(): boolean {
    return this.bulkJobData?.assetTypeId != null && !this.bulkJobData.cancelled;
  }

  // EDUS-2625: Selected is the whole selection, including devices skipped as
  // ineligible before the job ran. Jobs without skip data (older jobs, actions
  // that never skip) fall back to the devices the job attempted. Rarely more
  // than Successful + Failed + Skipped: devices deleted before dispatch, and
  // devices in a crashed batch, are counted in none of those.
  get selectedCount(): number {
    return (
      this.bulkJobData?.requestedCount ??
      this.bulkJobData?.aggregationStatistics?.totalAttempted ??
      0
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hoverCardService.hideCard();
  }

  onAuditLogFiltersChanged(event: {
    filters: SelectedFilterField[];
    filterBarId: string;
  }): void {
    this.hoverCardService.hideCard();

    this.store.dispatch(
      FilterActions.updateFilters({
        filters: event.filters,
        filterBarId: this.auditLogFilterBarId,
      })
    );
  }

  onContainerMouseLeave(): void {
    this.hoverCardService.hideCard();
  }

  onRowClicked(event: RowClickedEvent): void {
    const auditLogData = event.data as AuditLogWithUser;
    if (auditLogData?.id && auditLogData.user) {
      const userData: UserProfileCardData = {
        name: auditLogData.user.name,
        title: auditLogData.user.role || 'N/A',
        email: auditLogData.user.email,
      };

      // EDUS-2625: Bulk-job rows (jobId != null) open the aggregation-stats drawer.
      // Single/auth rows fall through to the existing per-user drawer.
      if (auditLogData.jobId) {
        this.onBulkJobClick(auditLogData, userData);
      } else {
        this.onUserClick(auditLogData.user.id, userData, auditLogData);
      }
    }
  }

  // Populate the drawer with the bulk-job aggregation content (EDUS-2625, Figma 14618:238105)
  onBulkJobClick(
    auditLogData: AuditLogWithUser,
    userData: UserProfileCardData
  ): void {
    this.isBulkJobDrawer = true;
    this.bulkJobData = auditLogData;
    this.selectedUserData = userData;
    this.drawerTitle = auditLogData.action || 'Details';
    this.drawerSubtitle = '';
    this.drawerContents = [];

    this.store.dispatch(openDrawer({ id: 'auditLogDrawer' }));
    this.drawerNotifications.notifyOpenDrawer('auditLogDrawer');
  }

  // Navigates to the full job page from the bulk-job drawer's Details button.
  onJobClick(auditLogData: AuditLogWithUser): void {
    const currentPage = this.auditLogSearchGrid?.getCurrentPage() ?? 0;
    sessionStorage.setItem('auditLogReturnPage', currentPage.toString());
    // EDUS-2673: persist the drawer's row + user payload so "Back to Audit Logs" can re-open it.
    // Serialize snapshot rather than re-fetch — avoids an extra network round-trip on return.
    sessionStorage.setItem(
      'auditLogReturnDrawer',
      JSON.stringify({
        auditLogData,
        userData: this.selectedUserData,
      })
    );
    this.router.navigate(['/administration/audit-log/job', auditLogData.jobId], {
      queryParams: {
        auditLogId: auditLogData.id,
        assetTypeId: auditLogData.assetTypeId,
        actionName: auditLogData.action || '',
      },
    });
  }

  onViewJobDetailsClick(): void {
    if (this.bulkJobData) {
      this.onJobClick(this.bulkJobData);
    }
  }

  onUserClick(
    userId: number,
    userData: UserProfileCardData,
    singleAuditLog?: AuditLogWithUser
  ): void {
    this.isBulkJobDrawer = false;
    this.bulkJobData = null;
    this.selectedUserId = userId;
    this.selectedUserData = userData;

    this.drawerTitle = singleAuditLog?.action || 'Details';
    this.drawerSubtitle = '';

    if (singleAuditLog) {
      this.drawerContents = [this.formatAuditLogEntry(singleAuditLog)];

      this.store.dispatch(openDrawer({ id: 'auditLogDrawer' }));
      this.drawerNotifications.notifyOpenDrawer('auditLogDrawer');
      return;
    }

    this.drawerContents = [];

    this.auditLogDataService
      .getAuditLogsByUser(userId, 0, 100)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const logs = response.data || [];

          if (logs.length === 0) {
            this.drawerContents = [
              {
                cellTitle: '',
                cellData: ['No audit logs found for this user'],
                cellIcons: ['info'],
              },
            ];
          } else {
            this.drawerContents = logs.map((log) =>
              this.formatAuditLogEntry(log)
            );
          }
        },
        error: () => {
          this.drawerContents = [
            {
              cellTitle: '',
              cellData: ['Failed to load audit logs'],
              cellIcons: ['error'],
            },
          ];
        },
      });

    this.store.dispatch(openDrawer({ id: 'auditLogDrawer' }));
    this.drawerNotifications.notifyOpenDrawer('auditLogDrawer');
  }

  closeDrawer(): void {
    this.store.dispatch(closeDrawer({ id: 'auditLogDrawer' }));
    this.drawerNotifications.notifyCloseDrawer('auditLogDrawer');
  }

  onGridLoadError(error: Error): void {
    this.snackbarService.showError(
      error?.message
        ? `Failed to load audit logs: ${error.message}`
        : 'Failed to load audit logs.'
    );
  }

  private formatAuditLogEntry(log: AuditLogWithUser): DrawerContent {
    const timeAgo = log.createdAt
      ? formatTimeAgo(log.createdAt)
      : 'Unknown time';

    const cellData = [
      timeAgo,
      `${log.category || 'N/A'} - ${log.action || 'N/A'}`,
    ];
    const dataFontWeights = ['700', '700'];
    const dataBottomBorders = [true, false];

    if (log.actionData) {
      try {
        const parsedData = JSON.parse(log.actionData);
        if (parsedData.description) {
          cellData.push(parsedData.description);
          dataFontWeights.push('400');
          dataBottomBorders.push(false);
        }
      } catch {
        // If parsing fails, actionData might not be JSON
      }
    }

    return {
      cellData,
      cellIcons: [],
      showBottomBorder: false,
      showBorder: false,
      dataFontSize: '14px',
      dataFontFamily: 'Source Sans 3',
      dataFontWeights,
      dataBottomBorders,
      dataBottomBorderPadding: '0px',
      dataLineHeight: '1.5',
      dataPaddingY: '0',
    };
  }
}
